import { useTranslation } from "react-i18next";
import type { PlanCreditStep } from "@/lib/pricingPlans";

/**
 * The credit slider on a plan card (Pro: 9,000 / 15,000 / 22,000 / 40,000 a
 * month). A row of stops rather than a drag handle: four fixed prices, each
 * its own plan, and it stays tappable at phone width.
 */
export default function CreditStepPicker({
  steps,
  value,
  onChange,
  className = "",
}: {
  steps: PlanCreditStep[];
  value: string;
  onChange: (stepId: string) => void;
  className?: string;
}) {
  const { t, i18n } = useTranslation();
  const compact = new Intl.NumberFormat(i18n.language, {
    notation: "compact",
    maximumFractionDigits: 1,
  });
  return (
    <div
      role="radiogroup"
      aria-label={t("pricing.creditStepsLabel", {
        defaultValue: "Monthly credits",
      })}
      className={`grid gap-1 rounded-full bg-muted/60 p-0.5 ${className}`}
      style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}
    >
      {steps.map((step) => {
        const active = step.id === value;
        return (
          <button
            key={step.id}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={t("pricing.creditsMonthlyHeadline", {
              count: step.credits,
              defaultValue: `${step.credits.toLocaleString(i18n.language)} monthly credits`,
            })}
            data-credit-step={step.id}
            onClick={() => onChange(step.id)}
            className={`rounded-full py-1 text-[11px] font-semibold tabular-nums transition-colors ${
              active
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {compact.format(step.credits)}
          </button>
        );
      })}
    </div>
  );
}
